import { RaidStageKind as RaidStageKindValue } from '../types'
import type {
  BuildPreset,
  BuildPresetStage,
  RaidStage,
  RaidStageKind,
} from '../types'
import type { BuildStageSelection } from './raidStageBuilds'
import { isDistinctWeaponPair, sameWeaponPair } from './playerWeaponBuilds'

export function weaponPairKey(weaponSlugs: ReadonlyArray<string>): string {
  return [...weaponSlugs].sort().join('|')
}

export function uniqueWeaponPairs(
  builds: ReadonlyArray<ReadonlyArray<string>>,
): ReadonlyArray<ReadonlyArray<string>> {
  const seen = new Set<string>()
  return builds.filter((build) => {
    if (!isDistinctWeaponPair(build)) return false
    const key = weaponPairKey(build)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function presetStage(
  preset: BuildPreset,
  kind: RaidStageKind,
): BuildPresetStage | null {
  return preset.stages.find((stage) => stage.stageKind === kind) ?? null
}

/** Opening is not configured separately; it inherits the Towers/ALL pair. */
export function presetStageForRaidKind(
  preset: BuildPreset,
  kind: RaidStageKind,
): BuildPresetStage | null {
  return presetStage(
    preset,
    kind === RaidStageKindValue.Opening ? RaidStageKindValue.MidPush : kind,
  )
}

/** True when the raid's effective build for every actual stage matches the preset. */
export function buildPresetMatchesRaid(
  preset: BuildPreset,
  stages: ReadonlyArray<RaidStage>,
  selections: ReadonlyArray<BuildStageSelection>,
): boolean {
  if (stages.length === 0) return false
  return stages.every((stage) => {
    const expected = presetStageForRaidKind(preset, stage.kind)?.weaponSlugs
    const actual = selections.find((selection) => selection.stageId === stage.id)?.weaponSlugs
    return expected !== undefined
      && actual !== undefined
      && sameWeaponPair(expected, actual)
  })
}
